import React, { useMemo } from 'react';

import { FilterDropdown } from '@/components/filters/FilterDropdown';
import { ClearFiltersButton } from '@/components/legacy-ui/ClearFiltersButton';
import { FilterOption, GroupOption } from '@/hooks/api/useGetAssetFilters';
import { SeedFilter } from '@/hooks/useSeeds';
import {
  getFilterDropdownWidth,
  shouldShowFilterField,
} from '@/sections/seeds/config';
import { SeedType } from '@/sections/seeds/types';
import { useAuth } from '@/state/auth';
import { useEntityFilters } from '@/utils/entityFilters.util';
import { collapseRegistrarFilterValues } from '@/utils/formatRegistrar.util';
import { omit } from '@/utils/lodash.util';
import {
  collapseMergedFilterValues,
  expandMergedFilterValues,
} from '@/utils/mergeFilterOptions.util';

// Filter keys whose options are merged by display label (value = canonical
// label, raw backend variants in rawValues) — selections must be expanded
// back to raws at apply time and stored raws collapsed for display.
// Registrar keeps its options-free collapse (legacy "name [Tag = X]" values
// must collapse even when absent from current options); registrant/customer
// use the options-driven merged collapse. Expansion is the same shared
// algorithm for all three (expandRegistrarFilterValues delegates to
// expandMergedFilterValues).
const MERGED_SEED_KEYS = new Set(['registrar', 'registrant', 'customer']);

type MergedOptionLike = ReadonlyArray<{
  value?: string | number;
  rawValues?: string[];
}>;

interface SeedsFiltersProps {
  filters: SeedFilter;
  setFilters: React.Dispatch<React.SetStateAction<SeedFilter>>;
  seedType?: SeedType;
}

// Map filter keys to data-tutorial attribute values for the tutorial system
const FILTER_TUTORIAL_ATTRS: Record<string, string> = {
  status: 'seeds-filter-status',
  origins: 'seeds-filter-source',
  tags: 'seeds-filter-tags',
  profile: 'seeds-filter-profile',
  email: 'seeds-filter-email',
  registrar: 'seeds-filter-registrar',
  registrant: 'seeds-filter-registrant',
  country: 'seeds-filter-country',
  customer: 'seeds-filter-customer',
};

/**
 * Seeds filter bar component with advanced filters
 *
 * Note: Renders only filter dropdowns. Used inside TableControlsContainer.
 * SearchBar is passed to TableControlsContainer's filtersRight prop.
 *
 * **Performance Optimized**: React.memo prevents re-renders when props unchanged
 */
export const SeedsFilters: React.FC<SeedsFiltersProps> = React.memo(
  ({ filters, setFilters, seedType }) => {
    const {
      filterOptions,
      sortedOptions,
      status: filterOptionsStatus,
    } = useEntityFilters('Seed', 'seed-table');
    const { isPraetorianUser, isDemoOrg } = useAuth();

    // Filter options based on the seed type using centralized configuration
    // Domain tab filters: status, source, email, registrar, registrant
    // IP/CIDR tab filters: status, source, country, registrar, registrant
    // WebApplication tab filters: status, source
    const filteredSortedOptions = useMemo(() => {
      if (!seedType) return sortedOptions;

      return sortedOptions.filter(option => {
        const fieldName = option.value as string;
        return shouldShowFilterField(fieldName, seedType);
      });
    }, [sortedOptions, seedType]);

    // ENG-5107: Clear Filters appears when a search value OR any advanced
    // filter is active, and clears both. Derived directly — the React
    // Compiler memoizes; a manual useMemo here would add no value (it would
    // recompute on every `filters` change regardless) and fights the repo
    // convention (ui/CLAUDE.md: no manual memoization for re-render
    // correctness).
    const hasSearch = Boolean(filters.search?.trim());
    const hasDropdownFilters =
      filters.advancedFilters &&
      Object.values(filters.advancedFilters).some(value =>
        Boolean(value?.length)
      );
    const isClearable = hasSearch || hasDropdownFilters;

    return (
      <>
        {filteredSortedOptions.map((option, index) => {
          const key = option.value as string;
          const filterOption =
            filterOptions &&
            typeof filterOptions === 'object' &&
            !Array.isArray(filterOptions)
              ? filterOptions[key]
              : null;
          if (!filterOption) return null;

          const tutorialAttr = FILTER_TUTORIAL_ATTRS[key];

          const dropdown = (
            <FilterDropdown
              key={index}
              id={key}
              isLoading={filterOptionsStatus === 'pending'}
              label={option.label}
              options={filterOption as (FilterOption | GroupOption)[]}
              isPraetorianUser={isPraetorianUser && !isDemoOrg}
              selectedValues={
                key === 'registrar'
                  ? // Stored registrar values are raw backend values (expanded);
                    // collapse them back to formatted labels so the matching
                    // option (whose value is the formatted label) shows selected.
                    collapseRegistrarFilterValues(
                      (filters.advancedFilters?.registrar as
                        | string[]
                        | undefined) || []
                    )
                  : MERGED_SEED_KEYS.has(key)
                    ? // Stored registrant/customer values are raw backend values;
                      // collapse them to canonical labels via the options'
                      // rawValues membership so the merged option shows selected.
                      collapseMergedFilterValues(
                        (filters.advancedFilters?.[key] as
                          | string[]
                          | undefined) || [],
                        filterOption as MergedOptionLike
                      )
                    : filters.advancedFilters?.[
                        key as keyof typeof filters.advancedFilters
                      ] || []
              }
              onApply={selected => {
                const selectedValues = selected.map(
                  option => option.value?.toString() || ''
                );
                // Merged options (registrar/registrant/customer) are grouped by
                // display label; expand the selection back to every raw backend
                // value in its group so the IN-filter matches all raw variants
                // (also under negation: NOT-of-the-label excludes every variant).
                const values = MERGED_SEED_KEYS.has(key)
                  ? expandMergedFilterValues(
                      selectedValues,
                      filterOption as MergedOptionLike
                    )
                  : selectedValues;

                setFilters(prev => ({
                  ...prev,
                  advancedFilters:
                    values.length > 0
                      ? {
                          ...prev.advancedFilters,
                          [key]: values,
                        }
                      : omit(prev.advancedFilters, key),
                }));
              }}
              onClear={() => {
                setFilters(prev => ({
                  ...prev,
                  advancedFilters: omit(prev.advancedFilters, key),
                  negatedFilterKeys: prev.negatedFilterKeys?.filter(
                    k => k !== key
                  ),
                }));
              }}
              isLoadingOptions={filterOptionsStatus === 'pending'}
              width={getFilterDropdownWidth(key)}
              isNegated={filters.negatedFilterKeys?.includes(key) ?? false}
              onToggleNegation={() => {
                setFilters(prev => ({
                  ...prev,
                  negatedFilterKeys: prev.negatedFilterKeys?.includes(key)
                    ? prev.negatedFilterKeys.filter(k => k !== key)
                    : [...(prev.negatedFilterKeys ?? []), key],
                }));
              }}
            />
          );

          if (tutorialAttr) {
            return (
              <div data-tutorial={tutorialAttr} key={index}>
                {dropdown}
              </div>
            );
          }

          return dropdown;
        })}
        {isClearable && (
          <ClearFiltersButton
            onClick={() =>
              setFilters(filters => {
                return {
                  ...filters,
                  search: '',
                  advancedFilters: {},
                  negatedFilterKeys: [],
                };
              })
            }
          />
        )}
      </>
    );
  }
);

SeedsFilters.displayName = 'SeedsFilters';
